import { Row } from '@/libs/supabase/supabase.interface'

export type IDailySalesSummary = Row<'DailySalesSummary'>
export type IDailyExpense = Row<'DailyExpense'>
export type IOrderBook = Row<'OrderBook'>
export type ICustomer = Row<'Customer'>
export type IDailyCustomerSummary = Row<'DailyCustomerSummary'>

// PaymentMethod enum (TypeScript) - mirrors Prisma PaymentMethod enum
export enum PaymentMethod {
  CASH = 'CASH',
  TRANSFER = 'TRANSFER',
}

// PaymentEntry interface - represents a single payment record from the database
export interface IPaymentEntry {
  id: string
  dailyCustomerSummaryId: string
  orderBookId?: number
  amountTHB: number
  paymentMethod: PaymentMethod
  note?: string
  createdAt: string
  updatedAt: string
}

// Local state for PaymentEntry that hasn't been saved yet
export interface ILocalPaymentEntry {
  tempId: string // temporary ID for new entries (uuid)
  id?: string // real ID for existing entries
  dailyCustomerSummaryId?: string
  orderBookId?: number
  amountTHB: number
  paymentMethod: PaymentMethod
  note?: string
  status: 'new' | 'modified' | 'deleted' | 'unchanged'
}

// Batch save input for processing multiple PaymentEntry changes in a single Save action
export interface IBatchPaymentSaveInput {
  customerId: string
  date: string
  orderBookId?: number
  adds: Omit<ILocalPaymentEntry, 'tempId' | 'status'>[]
  updates: { id: string; amountTHB: number; paymentMethod: PaymentMethod; note?: string }[]
  deletes: string[] // PaymentEntry IDs to delete
}

export interface ICustomerSalesData {
  customerId: string
  customerName: string
  customerType: 'REGULAR' | 'GENERAL'
  customerRanking?: number
  
  // Product totals (from individual order, 0 for payment-only rows)
  largeIceCubeAmount: number
  smallIceCubeAmount: number
  crushedIceAmount: number
  coarselyCrushedIceAmount: number
  totalQuantity: number
  totalAmountTHB: number
  
  // Payment information (from DailyCustomerSummary, shared per customer)
  amountPaidTHB: number
  paymentStatus: 'CASH' | 'TRANSFER' | 'UNPAID'
  outstandingBalanceTHB: number
  
  // Metadata
  orderCount: number // 1 for order rows, 0 for payment-only rows
  paymentCount: number
  lastOrderTime?: string
  
  // References
  dailyCustomerSummaryId?: string // Reference to DailyCustomerSummary record
  orderId?: number // Reference to specific OrderBook record (undefined for payment-only rows)
  rowType: 'ORDER' | 'PAYMENT_ONLY' // Type of row

  // Void flag
  isVoidedFromSales: boolean // flag ว่าออเดอร์ถูก void จากการคำนวณยอดขายหรือไม่

  // Payment entries (from PaymentEntry records)
  paymentEntries: IPaymentEntry[]
}

export interface IPaymentSummaryData {
  totalCashPayments: number
  totalTransferPayments: number
  totalOutstandingBalance: number
  totalPayments: number
  remainingCashTHB: number
}

export interface IExpenseItem {
  id: string
  name: string
  amountTHB: number
  createdAt: string
  updatedAt: string
}

export interface IExpensesByType {
  FUEL: IExpenseItem[]
  EMPLOYEE_OVERTIME: IExpenseItem[]
  OTHER: IExpenseItem[]
}

export interface IDailySalesFilter {
  date: string // Format: YYYY-MM-DD
}

export interface ICreateDailyExpenseInput {
  dailySummaryId: string
  expenseType: 'FUEL' | 'EMPLOYEE_OVERTIME' | 'OTHER'
  name: string
  amountTHB: number
}

export interface IUpdateDailyExpenseInput {
  expenseType?: 'FUEL' | 'EMPLOYEE_OVERTIME' | 'OTHER'
  name?: string
  amountTHB?: number
}

export interface IUpdateCustomerPaymentInput {
  customerId: string
  amountPaidTHB: number
  outstandingBalanceTHB: number
  orderId?: number // Optional: for order-specific updates
}

export interface ICreateDailyCustomerSummaryInput {
  date: string // YYYY-MM-DD
  customerId: string
  totalLargeIceCubeAmount?: number
  totalSmallIceCubeAmount?: number
  totalCrushedIceAmount?: number
  totalCoarselyCrushedIceAmount?: number
  totalAmountTHB?: number
  amountPaidTHB?: number
  outstandingBalanceTHB?: number
  orderCount?: number
  paymentCount?: number
}

export interface IUpdateDailyCustomerSummaryInput {
  amountPaidTHB?: number
  outstandingBalanceTHB?: number
}

export interface IDailySalesExportData {
  date: string
  customers: ICustomerSalesData[]
  paymentSummary: IPaymentSummaryData
  expenses: IExpensesByType
}